import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useMatches, useNavigate, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import { didYouMean, parseLang } from "@majourneys/shared";
import { CarFront, MapPin, Search } from "lucide-react";
import { activitiesApi } from "../../features/activities/api/activities.api";
import { activityKeys } from "../../features/activities/constants/query-keys";
import { transferRoutesQueryOptions } from "../../features/transport/api/transfers.api";
import { TRANSPORT_CITY } from "../../features/transport/lib/routes";
import {
  CITY_PAGE_ROUTE_IDS,
  matchTransfers,
  normalizeSearchText,
  searchResultsTarget,
} from "./header-search-target";
import { floatPanelStyle, popOrigin, useMenu, usePresence, type MenuGroup } from "../ui/use-presence";
import { BEVEL_INSET_NAVY, BEVEL_NAVY_FACE } from "@majourneys/shared/ui";

const NAVY = "#110E39";
const MUTED = "#5a5d77";
const HAIRLINE = "rgba(17,14,57,0.09)";
const SOFT_TINT = "rgba(17,14,57,0.055)";
const PRIMARY_SHADOW = `0 10px 24px -12px rgba(17,14,57,0.8), ${BEVEL_INSET_NAVY}`;
/** The field sinks into the bar at rest; a navy ring lifts it while focused. */
const FIELD_REST = `inset 0 1px 0 ${HAIRLINE}`;
const FIELD_FOCUS = `inset 0 1px 0 ${HAIRLINE}, 0 0 0 1.5px rgba(17,14,57,0.6), 0 0 0 5px rgba(17,14,57,0.08)`;
/** On a panel the field is a control: white, one hairline, the same focus ring. */
const FIELD_REST_CONTROL = "0 0 0 1px rgba(17,14,57,0.16)";
const FIELD_FOCUS_CONTROL = "0 0 0 1.5px rgba(17,14,57,0.6), 0 0 0 5px rgba(17,14,57,0.08)";

/**
 * Typo-tolerant token match: true when `token` is a prefix of `word` or within
 * an edit distance of 1 (one insert/delete/substitution). Keeps "casablance"
 * matching "casablanca" without pulling in unrelated words. Skips fuzzy checks
 * for very short tokens (≤2 chars) where edit distance is too permissive.
 */
function isNearMatch(token: string, word: string): boolean {
  if (word.startsWith(token) || token.startsWith(word)) return true;
  if (token.length <= 2) return false;
  if (Math.abs(token.length - word.length) > 1) return false;
  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < token.length && j < word.length) {
    if (token[i] === word[j]) {
      i += 1;
      j += 1;
      continue;
    }
    edits += 1;
    if (edits > 1) return false;
    if (token.length === word.length) {
      i += 1;
      j += 1;
    } else if (token.length > word.length) {
      i += 1;
    } else {
      j += 1;
    }
  }
  edits += token.length - i + (word.length - j);
  return edits <= 1;
}

interface SearchItem {
  id: string;
  slug: string;
  title: string;
  images: string[];
  /** Null on explore rows (restaurants, places to visit…), which have a segment instead. */
  category: { id: string; name: string; slug: string } | null;
  city: { id: string; name: string; slug: string } | null;
  segment?: string | null;
}

interface CityItem {
  id: string;
  slug: string;
  name: string;
  image?: string | null;
  activitiesCount?: number;
}

/**
 * One row of the list. Cities and activities share the keyboard cursor and the
 * same markup, so the two cannot drift apart as one of them gains a field.
 */
type Suggestion = {
  /**
   * `transfer` is one ride; `transport` the hub with every ride. `place` is an
   * explore row (a restaurant, a place to visit): it has no booking page of
   * its own to sell, so it opens on Discover.
   */
  kind: "city" | "activity" | "place" | "transfer" | "transport";
  key: string;
  slug: string;
  title: string;
  image?: string | null;
  meta: string;
};

/** The field sinks with the bar: 46 → 40, and its button with it. */
const FIELD_SINK =
  "transition-[height] duration-200 ease-[var(--mj-ease-out)] motion-reduce:transition-none";

/** The title with the part the guest typed in bold. */
function MatchText({ text, query }: { text: string; query: string }) {
  const q = query.toLowerCase();
  const at = q.length >= 2 ? text.toLowerCase().indexOf(q) : -1;
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <span className="font-bold">{text.slice(at, at + q.length)}</span>
      {text.slice(at + q.length)}
    </>
  );
}

export function HeaderSearch({
  group,
  compact = false,
  onActiveChange,
  onDismiss,
  autoFocus = false,
  focusRing = true,
  surface = "sunken",
  listId = "header-search-suggestions",
}: {
  /**
   * `sunken` sinks into the header bar; `control` is the white field with one
   * hairline that sits on a pewter panel (the error page).
   */
  surface?: "sunken" | "control";
  /** The suggestions list id; a second search on the page needs its own. */
  listId?: string;
  /**
   * The navy ring the field draws while focused. Off in the minimal phone
   * search: the field has just grown out of the button, so it is plainly the
   * active control, and the ring easing in read as a stray line at the edge.
   */
  focusRing?: boolean;
  group?: MenuGroup;
  compact?: boolean;
  /** Focus the field on mount: the minimal phone bar opens straight into it. */
  autoFocus?: boolean;
  /** Fires while the suggestions are open, so the bar can give up its row. */
  onActiveChange?: (active: boolean) => void;
  /**
   * The guest is done with the field: picked a suggestion, pressed Escape, or
   * left it empty. Where the field grew out of a button, this folds it back.
   */
  onDismiss?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { lang } = (useParams({ strict: false }) as unknown) as { lang?: string };
  const locale = lang ?? "en";
  const navigate = useNavigate();
  // The city of the page the guest is on, when it is a city page or a city
  // hub: Enter keeps the results in it.
  const pageCity = useMatches({
    select: (matches) => {
      const m = matches.find((x) => CITY_PAGE_ROUTE_IDS.has(x.routeId as string));
      return (m?.params as { city?: string } | undefined)?.city;
    },
  });
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState("");
  const fullPlaceholder = t("nav.search_placeholder");
  const shortPlaceholder = t("common.search");
  /**
   * The full hint ("City, activity or restaurant") is cut on the narrowest
   * fields: the 404 page at 320px left 157px for ~220px of German or Dutch.
   * There the field says just "Search" in the page's language. Measured after
   * mount, so the server HTML (always the full hint) still matches.
   */
  const [hintFits, setHintFits] = useState(true);
  useEffect(() => {
    const input = inputRef.current;
    if (!input || typeof ResizeObserver === "undefined") return undefined;
    const measure = () => {
      const cs = getComputedStyle(input);
      const ctx = document.createElement("canvas").getContext("2d");
      if (!ctx) return;
      ctx.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
      const room = input.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
      setHintFits(room <= 0 || ctx.measureText(fullPlaceholder).width <= room);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(input);
    return () => ro.disconnect();
  }, [fullPlaceholder]);
  /**
   * The row the cursor is on; -1 is none. Enter opens a row only when the
   * guest moved to it with the arrow keys. A highlight from the mouse, or the
   * first row simply being first, is not a choice: Enter then opens the
   * results page for the words typed.
   */
  const [activeIdx, setActiveIdx] = useState(-1);
  const [keyed, setKeyed] = useState(false);
  const [focused, setFocused] = useState(false);
  // The suggestions are one of the bar's menus: opening them closes the rest.
  const menu = useMenu<HTMLFormElement>(group, "search");
  const open = menu.open;

  const typed = query.trim();
  /** The query the guest asked to keep as typed, after "Search for … instead". */
  const [kept, setKept] = useState<string | null>(null);

  /**
   * The city list, on the same cache key the city pages use, so opening the
   * search on a page that already has it costs nothing. Cities change about
   * never, hence the long staleTime.
   */
  const { data: citiesData } = useQuery({
    queryKey: [...activityKeys.cities(), i18n.language],
    queryFn: () => activitiesApi.listCities(i18n.language),
    staleTime: 30 * 60_000,
    enabled: open,
  });

  const cities = useMemo<readonly CityItem[]>(() => {
    const list = (citiesData as { data?: CityItem[] } | CityItem[] | undefined);
    const rows = Array.isArray(list) ? list : list?.data;
    return Array.isArray(rows) ? rows : [];
  }, [citiesData]);

  /*
   * "Did you mean Essaouira?" (owner, 2026-10-26, idea 77): a place name two
   * letters off ("esauira") found nothing, which reads as "we do not go
   * there". The search runs on the corrected words and says so, with a way
   * back to what was typed. Only places with something to book are offered.
   */
  const correction = useMemo(
    () =>
      typed.length >= 4 && kept !== typed
        ? didYouMean(
            typed,
            cities
              .filter((c) => c.slug && (c.activitiesCount ?? 0) > 0)
              .map((c) => ({ name: c.name, weight: c.activitiesCount ?? 0 })),
          )
        : null,
    [typed, kept, cities],
  );
  const trimmed = correction?.query ?? typed;

  // The language goes with the query so titles and category names come back
  // in it: on /fr they were English.
  const filters = useMemo(
    () => (trimmed ? { q: trimmed, page_size: "8", lang: locale } : { page_size: "12", lang: locale }),
    [trimmed, locale],
  );
  const { data, isFetching } = useQuery({
    queryKey: activityKeys.list(filters),
    queryFn: () => activitiesApi.list(filters),
    staleTime: 60_000,
    // Only fetch when the dropdown is actually open. Avoids burning a 50-item
    // activity fetch + thumbnail download on every page load before the user
    // ever interacts with the header search.
    enabled: open && (trimmed.length === 0 || trimmed.length >= 2),
  });

  /*
   * Places with nothing to book (Briganti, a museum, a riad we only list)
   * live on Discover, and the activity search above is bookable-only, so
   * "briganti" said "No results" while Discover showed it. A second, small
   * query for explore rows fills that in; a pick opens the place on Discover.
   */
  const placeFilters = useMemo(
    () => ({ q: trimmed, page_size: "6", mode: "explore" as const, lang: locale }),
    [trimmed, locale],
  );
  const { data: placeData, isFetching: placesFetching } = useQuery({
    queryKey: activityKeys.list(placeFilters),
    queryFn: () => activitiesApi.list(placeFilters),
    staleTime: 60_000,
    enabled: open && trimmed.length >= 2,
  });

  /*
   * Transfers are sold from /transport only, so the activity search never
   * returns them. A query that asks for a ride ("transfer", "aéroport",
   * "essaouira transfer") is matched against the route list instead. The
   * same cached list the transport pages use.
   */
  const { data: transferRoutes } = useQuery({
    ...transferRoutesQueryOptions(TRANSPORT_CITY, parseLang(locale)),
    staleTime: 10 * 60_000,
    enabled: open && trimmed.length >= 3,
  });
  const transfer = useMemo(
    () => (trimmed.length >= 3 ? matchTransfers(trimmed, transferRoutes ?? []) : null),
    [trimmed, transferRoutes],
  );

  const items = useMemo<readonly SearchItem[]>(() => {
    const list = (data as { data?: SearchItem[] } | undefined)?.data;
    return Array.isArray(list) ? list : [];
  }, [data]);
  const places = useMemo<readonly SearchItem[]>(() => {
    const list = (placeData as { data?: SearchItem[] } | undefined)?.data;
    return Array.isArray(list) ? list.filter((p) => p.slug) : [];
  }, [placeData]);

  /**
   * Cities first, and only when they actually match.
   *
   * A guest typing "marrakech" wants the city before any one tour in it — the
   * city page is the wider answer and every tour is reachable from it. Two at
   * most, so a query that matches several places cannot push the activities
   * off the list.
   */
  const cityHits = useMemo<readonly Suggestion[]>(() => {
    const q = trimmed.toLowerCase();
    if (q.length < 2) return [];
    const tokens = q.split(/\s+/).filter(Boolean);
    const scored = cities
      // A city hub with nothing bookable in it is a dead end, and it is also
      // where the seed/test rows live.
      .filter((c) => c.slug && (c.activitiesCount ?? 0) > 0)
      .map((c) => {
        const name = c.name.toLowerCase();
        let score = 0;
        for (const token of tokens) {
          if (name.startsWith(token)) score += 3;
          else if (name.includes(token)) score += 2;
          else if (isNearMatch(token, name)) score += 1;
        }
        return { c, score };
      })
      .filter((s) => s.score > 0)
      .sort((a, b) => b.score - a.score || (b.c.activitiesCount ?? 0) - (a.c.activitiesCount ?? 0));
    return scored.slice(0, 2).map(({ c }) => ({
      kind: "city" as const,
      key: `city:${c.id}`,
      slug: c.slug,
      title: c.name,
      image: c.image,
      meta: `${t("nav.search_city", { defaultValue: "City" })} · ${t("home.collections.count", {
        count: c.activitiesCount ?? 0,
      })}`,
    }));
  }, [cities, trimmed, t]);

  /**
   * Bookable activities and explore places, ranked together. Smart match:
   * score each row by how many query tokens it satisfies, in any order,
   * tolerating small typos. A contained token scores best, a near-miss (edit
   * distance <= 1 against any haystack word: "casablance" -> "casablanca")
   * still counts. On a tie the bookable row goes first, then backend order.
   * When nothing matches at all the bookable rows still show, as before, so
   * one mistyped word cannot empty the list.
   */
  const rowHits = useMemo<readonly { it: SearchItem; place: boolean }[]>(() => {
    const q = trimmed.toLowerCase();
    if (q.length < 1) return [];
    const tokens = q.split(/\s+/).filter(Boolean);
    const score = (it: SearchItem) => {
      // The slug too: on /ar the title is "بريغانتي", and "brigan" typed in
      // Latin letters still has to find it.
      const haystack =
        `${it.title} ${it.slug.replace(/-/g, " ")} ${it.city?.name ?? ""} ${it.category?.name ?? ""}`.toLowerCase();
      const words = haystack.split(/\s+/).filter(Boolean);
      let n = 0;
      for (const token of tokens) {
        if (haystack.includes(token)) n += 2;
        else if (words.some((w) => isNearMatch(token, w))) n += 1;
      }
      return n;
    };
    const scored = [
      ...items.map((it, idx) => ({ it, place: false, score: score(it), idx })),
      ...places
        .filter((p) => !items.some((it) => it.id === p.id))
        // The server already matched them, in every language's title, so
        // they count even when no token lands in the words shown here.
        .map((it, idx) => ({ it, place: true, score: Math.max(1, score(it)), idx: 1000 + idx })),
    ];
    const matched = scored.filter((s) => s.score > 0);
    const ranked = (matched.length > 0 ? matched : scored).sort(
      (a, b) => b.score - a.score || a.idx - b.idx,
    );
    return ranked.slice(0, 6);
  }, [items, places, trimmed]);

  const transferHits = useMemo<readonly Suggestion[]>(() => {
    if (!transfer?.intent) return [];
    const rows: Suggestion[] = transfer.routes.map((r) => ({
      kind: "transfer" as const,
      key: `transfer:${r.slug}`,
      slug: r.slug,
      title: r.title,
      meta: t("nav.transport"),
    }));
    rows.push({
      kind: "transport",
      key: "transport",
      slug: "",
      title: t("nav.search_transfers"),
      meta: t("nav.transport"),
    });
    return rows;
  }, [transfer, t]);

  /** The query names a city other than the page's, so the results leave it. */
  const namesOtherCity = useMemo(() => {
    if (!pageCity) return false;
    const tokens = normalizeSearchText(trimmed).split(/\s+/).filter((w) => w.length >= 4);
    return cities.some(
      (c) =>
        c.slug &&
        c.slug !== pageCity &&
        tokens.some((w) => normalizeSearchText(c.name).startsWith(w) || c.slug.startsWith(w)),
    );
  }, [cities, trimmed, pageCity]);

  const suggestions = useMemo<readonly Suggestion[]>(() => {
    const rows: Suggestion[] = [...transferHits, ...cityHits];
    for (const { it, place } of rowHits) {
      if (rows.length >= 6) break;
      const kindLabel = place && it.segment ? t(`ai_concierge.kind.${it.segment}`, { defaultValue: "" }) : "";
      rows.push({
        kind: place ? "place" : "activity",
        key: `${place ? "place" : "activity"}:${it.id}`,
        slug: it.slug,
        title: it.title,
        image: it.images?.[0],
        meta: [place ? kindLabel : it.category?.name, it.city?.name].filter(Boolean).join(" · "),
      });
    }
    return rows;
  }, [transferHits, cityHits, rowHits, t]);

  /*
   * Only places we list matched: the results page sells bookable things and
   * would say "No results" for "briganti". Enter opens the top place on
   * Discover instead, and the "See all results" row, a dead end, goes.
   */
  const placesOnly = suggestions.length > 0 && suggestions.every((s) => s.kind === "place");

  useEffect(() => {
    setActiveIdx(-1);
    setKeyed(false);
  }, [query]);

  useEffect(() => {
    onActiveChange?.(open);
  }, [open, onActiveChange]);

  // Nothing we sell matches: say so, rather than an empty field that looks
  // broken ("surfles" showed nothing at all).
  const noResults =
    open && typed.length >= 2 && !isFetching && !placesFetching && suggestions.length === 0 && !!data;
  const listed = open && (suggestions.length > 0 || noResults);
  const panel = usePresence(listed);

  /*
   * The highlight is one layer that slides to the active row instead of each
   * row painting its own background, so moving the mouse or the arrow keys
   * reads as one thing travelling down the list. Measured, not computed from a
   * row height: a title that wraps on a phone makes its row taller.
   */
  const rowRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [hl, setHl] = useState({ top: 0, height: 0 });
  useLayoutEffect(() => {
    const row = activeIdx >= 0 ? rowRefs.current[activeIdx] : null;
    if (!listed || !row) {
      setHl({ top: 0, height: 0 });
      return;
    }
    setHl({ top: row.offsetTop + 6, height: row.offsetHeight });
  }, [activeIdx, listed, suggestions, panel.mounted]);
  // A new set of results arrives as a new list, so it replays the entrance.
  const resultKey = suggestions.map((s) => s.key).join("|");

  const go = (item: Suggestion) => {
    menu.close();
    setQuery("");
    onDismiss?.();
    if (item.kind === "city") {
      navigate({ to: "/$lang/place/$city", params: { lang: locale, city: item.slug } });
      return;
    }
    if (item.kind === "transfer") {
      navigate({ to: "/$lang/transport/$slug", params: { lang: locale, slug: item.slug } });
      return;
    }
    if (item.kind === "transport") {
      navigate({ to: "/$lang/transport", params: { lang: locale } });
      return;
    }
    if (item.kind === "place") {
      // Discover opens the place's panel from `?item=` (DiscoverUrlSync).
      navigate({ to: "/$lang/discover", params: { lang: locale }, search: { item: item.slug } });
      return;
    }
    navigate({ to: "/$lang/$slug", params: { lang: locale, slug: item.slug } });
  };

  /** The results page for the words typed (see header-search-target.ts). */
  const openResults = () => {
    // The corrected words when a place was misspelled, as the list showed.
    const target = searchResultsTarget({
      lang: locale,
      query: trimmed,
      citySlug: pageCity,
      namesOtherCity,
      transfer,
    });
    menu.close();
    setQuery("");
    onDismiss?.();
    navigate(target as never);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const picked = keyed && activeIdx >= 0 ? suggestions[activeIdx] : undefined;
    if (picked) {
      go(picked);
      return;
    }
    // An empty field goes nowhere: the search button on a city page used to
    // leave the city. The field stays open for the words.
    if (!trimmed) {
      menu.setOpen(true);
      inputRef.current?.focus();
      return;
    }
    if (placesOnly) {
      go(suggestions[0]);
      return;
    }
    openResults();
  };

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      menu.close();
      onDismiss?.();
      return;
    }
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setKeyed(true);
      setActiveIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setKeyed(true);
      setActiveIdx((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    }
  };

  return (
    <form
      ref={menu.ref}
      role="search"
      autoComplete="off"
      onSubmit={handleSubmit}
      className="relative w-full"
    >
      <div
        className={`mj-field-focus flex items-center rounded-full ps-1 pe-[3px] ${FIELD_SINK} ${
          compact ? "h-[40px]" : "h-[46px]"
        }`}
        style={
          surface === "control"
            ? { background: "#fff", boxShadow: focused && focusRing ? FIELD_FOCUS_CONTROL : FIELD_REST_CONTROL }
            : { background: SOFT_TINT, boxShadow: focused && focusRing ? FIELD_FOCUS : FIELD_REST }
        }
      >
        <input
          ref={inputRef}
          type="text"
          name="search_query"
          value={query}
          aria-activedescendant={keyed && activeIdx >= 0 ? `${listId}-${activeIdx}` : undefined}
          onChange={(e) => {
            setQuery(e.target.value);
            menu.setOpen(true);
          }}
          onFocus={() => {
            setFocused(true);
            menu.setOpen(true);
          }}
          onBlur={(e) => {
            setFocused(false);
            const next = e.relatedTarget as Node | null;
            if (!query.trim() && !(next && menu.ref.current?.contains(next))) onDismiss?.();
          }}
          onKeyDown={onKey}
          autoFocus={autoFocus}
          placeholder={hintFits ? fullPlaceholder : shortPlaceholder}
          aria-label={t("common.search", { defaultValue: "Search" })}
          role="combobox"
          aria-autocomplete="list"
          aria-controls={listId}
          aria-expanded={listed}
          /* Safari will not shrink a flex input below its default twenty-character
             intrinsic width; see the note in `footer.tsx`. */
          size={1}
          className={`min-w-0 flex-1 bg-transparent px-[18px] text-[14px] font-semibold outline-none placeholder:font-normal placeholder:text-[#5a5d77] ${FIELD_SINK} ${
            compact ? "h-9" : "h-10"
          }`}
          style={{ color: NAVY }}
        />
        <button
          type="submit"
          aria-label={t("common.search", { defaultValue: "Search" })}
          className={`mj-press grid shrink-0 place-items-center rounded-full text-white ${FIELD_SINK} ${
            compact ? "h-9 w-9" : "h-10 w-10"
          }`}
          style={{ background: BEVEL_NAVY_FACE, boxShadow: PRIMARY_SHADOW }}
        >
          <Search size={17} strokeWidth={2.6} aria-hidden />
        </button>
      </div>

      {panel.mounted ? (
        <div
          ref={panel.ref}
          id={listId}
          role="listbox"
          data-state={panel.state}
          data-instant={menu.instant ? "true" : undefined}
          className={`mj-pop absolute start-1 end-1 top-[calc(100%+8px)] z-[var(--z-dropdown)] max-h-[300px] overflow-y-auto p-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${
            panel.state === "closed" ? "pointer-events-none" : ""
          }`}
          style={{ ...floatPanelStyle(18), transformOrigin: popOrigin("start", i18n.dir()) }}
        >
          {/* Two lines, not a title beside a pill. The pill was `shrink-0`, so
              on a phone it took the row and left the title as "Q…". The
              category and city now sit under the title, where narrow widths
              cost them nothing. */}
          <span
            aria-hidden
            className="mj-search-hl pointer-events-none absolute start-1.5 end-1.5 rounded-[12px]"
            style={{
              top: 0,
              height: hl.height,
              transform: `translateY(${hl.top}px)`,
              background: SOFT_TINT,
              opacity: hl.height ? 1 : 0,
            }}
          />
          <div key={resultKey} className="mj-stagger mj-stagger-blur relative">
            {correction ? (
              <div className="px-2.5 pb-1.5 pt-1">
                <p className="text-[13px] font-medium" style={{ color: NAVY }}>
                  <Trans
                    i18nKey="nav.did_you_mean"
                    values={{ place: correction.place }}
                    components={{ place: <span className="font-bold" /> }}
                  />
                </p>
                <button
                  type="button"
                  onClick={() => setKept(typed)}
                  className="mt-0.5 inline-flex min-h-[24px] items-center text-start text-[12px] font-semibold underline underline-offset-2"
                  style={{ color: MUTED }}
                >
                  {t("nav.search_instead", { query: typed })}
                </button>
              </div>
            ) : null}
            {suggestions.map((s, i) => (
              <button
                key={s.key}
                id={`${listId}-${i}`}
                ref={(el) => {
                  rowRefs.current[i] = el;
                }}
                type="button"
                role="option"
                aria-selected={keyed && i === activeIdx}
                onMouseEnter={() => {
                  setActiveIdx(i);
                  setKeyed(false);
                }}
                onClick={() => go(s)}
                className="flex w-full items-center gap-3 rounded-[12px] px-2.5 py-2 text-start"
              >
                {s.kind !== "activity" && s.kind !== "place" ? (
                  <span
                    className="grid h-[38px] w-[38px] shrink-0 place-items-center rounded-full"
                    style={{ background: SOFT_TINT, color: NAVY }}
                  >
                    {s.kind === "city" ? (
                      <MapPin size={17} strokeWidth={2.2} aria-hidden />
                    ) : (
                      <CarFront size={17} strokeWidth={2.2} aria-hidden />
                    )}
                  </span>
                ) : (
                  <img
                    src={s.image ?? "/images/default-avatar.png"}
                    alt=""
                    className="h-[38px] w-[38px] shrink-0 rounded-[10px] object-cover object-center"
                  />
                )}
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="line-clamp-2 break-words text-[13.5px] font-medium capitalize" style={{ color: NAVY }}>
                    <MatchText text={s.title} query={trimmed} />
                  </span>
                  {s.meta ? (
                    <span className="line-clamp-2 text-[11.5px] font-medium" style={{ color: MUTED }}>
                      {s.meta}
                    </span>
                  ) : null}
                </span>
              </button>
            ))}
            {noResults ? (
              <div className="px-2.5 py-2" role="status">
                <p className="text-[13.5px] font-semibold" style={{ color: NAVY }}>
                  {t("nav.search_no_results", { query: typed })}
                </p>
                <p className="mt-0.5 text-[12px] font-medium" style={{ color: MUTED }}>
                  {t("nav.search_no_results_hint")}
                </p>
              </div>
            ) : null}
            {/* What Enter does, said where the guest can see it and tap it. */}
            {typed.length >= 2 && suggestions.length > 0 && !placesOnly ? (
              <button
                type="button"
                onClick={openResults}
                className="mt-1 flex min-h-[40px] w-full items-center gap-3 rounded-[12px] px-2.5 py-2 text-start"
                style={{ borderTop: `1px solid ${HAIRLINE}` }}
              >
                <span
                  className="grid h-[38px] w-[38px] shrink-0 place-items-center rounded-full"
                  style={{ background: SOFT_TINT, color: NAVY }}
                >
                  <Search size={16} strokeWidth={2.4} aria-hidden />
                </span>
                <span className="min-w-0 flex-1 break-words text-[13.5px] font-semibold" style={{ color: NAVY }}>
                  {t("nav.search_see_all", { query: trimmed })}
                </span>
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </form>
  );
}
